<template>
  <div class="card mb-3">
    <div class="card-header d-flex align-items-center justify-content-between">
      <h5 class="mb-0">Attendance & Shift Defaults</h5>
      <button
        class="btn btn-sm btn-primary"
        type="button"
        :disabled="saving"
        @click="save"
      >
        <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
        Save Settings
      </button>
    </div>
    <div class="card-body">
      <div class="row g-3">
        <div class="col-md-3">
          <label class="form-label">Policy</label>
          <select v-model="form.policy" class="form-select">
            <option value="lenient">Lenient</option>
            <option value="strict">Strict</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label">Unscheduled Mode</label>
          <select v-model="form.unscheduledMode" class="form-select">
            <option value="default_shift">Default shift</option>
            <option value="free_clock">Free clock</option>
          </select>
        </div>
        <div class="col-md-2">
          <label class="form-label">Grace (min)</label>
          <input v-model.number="form.graceMinutes" type="number" min="0" class="form-control" />
        </div>
        <div class="col-md-2">
          <label class="form-label">Early window (min)</label>
          <input v-model.number="form.earlyWindowMinutes" type="number" min="0" class="form-control" />
        </div>
        <div class="col-md-2">
          <label class="form-label">Strict latest (min)</label>
          <input v-model.number="form.strictLatestAfterStart" type="number" min="0" class="form-control" />
        </div>
        <div class="col-md-2">
          <label class="form-label">Default start</label>
          <input v-model="form.defaultShiftStart" type="time" class="form-control" />
        </div>
        <div class="col-md-2">
          <label class="form-label">Default end</label>
          <input v-model="form.defaultShiftEnd" type="time" class="form-control" />
        </div>
        <div class="col-md-2">
          <label class="form-label">Break (min)</label>
          <input v-model.number="form.defaultShiftBreakMinutes" type="number" min="0" class="form-control" />
        </div>
        <div class="col-md-4 d-flex align-items-end">
          <div class="form-check form-switch">
            <input
              id="allowMultiShifts"
              v-model="form.allowMultipleShiftsPerDay"
              class="form-check-input"
              type="checkbox"
            />
            <label class="form-check-label" for="allowMultiShifts">Allow multiple shifts per day</label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import attendanceService from "@/services/hrm/attendanceEmployee.service";
import { toast } from "@/utils/toast";

const saving = ref(false);
const form = ref({
  policy: "lenient",
  unscheduledMode: "default_shift",
  graceMinutes: 10,
  earlyWindowMinutes: 30,
  strictLatestAfterStart: 60,
  defaultShiftStart: "09:00",
  defaultShiftEnd: "17:00",
  defaultShiftBreakMinutes: 60,
  allowMultipleShiftsPerDay: false,
});

function applySettings(data = {}) {
  form.value = {
    policy: data.policy ?? "lenient",
    unscheduledMode: data.unscheduledMode ?? "default_shift",
    graceMinutes: Number(data.graceMinutes ?? 10),
    earlyWindowMinutes: Number(data.earlyWindowMinutes ?? 30),
    strictLatestAfterStart: Number(data.strictLatestAfterStart ?? 60),
    defaultShiftStart: data.defaultShiftStart ?? "09:00",
    defaultShiftEnd: data.defaultShiftEnd ?? "17:00",
    defaultShiftBreakMinutes: Number(data.defaultShiftBreakMinutes ?? 60),
    allowMultipleShiftsPerDay: Boolean(data.allowMultipleShiftsPerDay ?? false),
  };
}

async function load() {
  try {
    const { data } = await attendanceService.getAttendanceSettings();
    applySettings(data);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to load attendance settings", "error");
  }
}

async function save() {
  saving.value = true;
  try {
    const { data } = await attendanceService.updateAttendanceSettings(form.value);
    applySettings(data);
    toast("Attendance settings saved", "success");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to save settings", "error");
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>
